import { createSelector } from '@ngrx/store';
import { Decimal } from 'decimal.js';

import { BetslipType } from '../../../../../core/betslip-type';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { PickId } from '../../../../../core/picks/pick-id';
import { IBetBuilderBetState } from '../../../../bet-builder/models';
import { comboBetStateSelector } from '../../../../combo-bet/selectors';
import { IComboBetState } from '../../../../combo-bet/state';
import { selectBetBuilderTypeState } from '../../../../linear-bet-builder/selectors';
import { betslipPicksListSelector, selectBetslipFlattenedPicksList } from '../../../../picks/selectors';
import { filterTypePicks } from '../../../../picks/services/linear-betslip-pick.utils';
import { singleBetStateSelector } from '../../../../single-bet/selectors';
import { ISingleBetState } from '../../../../single-bet/state';
import { teaserBetStateSelector } from '../../../../teaser-bet/selectors';
import { ITeaserBetState } from '../../../../teaser-bet/state';
import { betslipTypeStateSelector } from '../../../../types/selectors';
import { IBetslipTypeState } from '../../../../types/state';
import { selectRewardTokensList } from '../../../selectors';
import { TokenValidationInput } from '../criteria-validator-base.service';

export const selectBetstationValidationInputState = createSelector(
    selectRewardTokensList,
    betslipTypeStateSelector,
    betslipPicksListSelector,
    (tokens, types, pickList) => ({ tokens, types, pickList }),
);

export const selectSingleValidationInputState = createSelector(
    selectRewardTokensList,
    singleBetStateSelector,
    selectBetslipFlattenedPicksList,
    (tokens, singleBet, pickList) => ({ tokens, singleBet, pickList }),
);

export const selectComboValidationInputState = createSelector(
    selectRewardTokensList,
    comboBetStateSelector,
    betslipPicksListSelector,
    (tokens, comboBet, pickList) => ({ tokens, comboBet, pickList }),
);

export const selectTeaserValidationInputState = createSelector(
    selectRewardTokensList,
    teaserBetStateSelector,
    betslipPicksListSelector,
    (tokens, teaserBet, pickList) => ({ tokens, teaserBet, pickList }),
);

export const selectBetBuilderValidationInputState = createSelector(
    selectRewardTokensList,
    selectBetBuilderTypeState,
    betslipPicksListSelector,
    (tokens, betBuilder, pickList) => ({ tokens, betBuilder, pickList }),
);

interface ValidationInputState {
    types: IBetslipTypeState;
    pickList: BetslipPick[];
}

export function getComboBetValidationInput(comboBet: IComboBetState, picks: BetslipPick[]): TokenValidationInput {
    return {
        selectedPicks: filterTypePicks(comboBet.picks, picks, { isSelected: true }),
        allPicks: picks ?? [],
        selectedType: BetslipType.Combo,
        hasEachWay: comboBet.isEachWay,
        stake: calculateStateStake(comboBet),
        teaserTotalOdds: undefined,
    };
}

export function getTeaserBetValidationInput(teaserBet: ITeaserBetState, pickList: BetslipPick[]): TokenValidationInput {
    return {
        selectedPicks: filterTypePicks(teaserBet.picks, pickList, { isSelected: true }),
        allPicks: pickList ?? [],
        selectedType: BetslipType.Teaser,
        hasEachWay: false,
        stake: new Decimal(teaserBet.stake ?? 0),
        teaserTotalOdds: teaserBet.teaserPrice,
    };
}

export function getSingleBetValidationInput(singleBet: ISingleBetState, pickList: BetslipPick[], pickId: PickId): TokenValidationInput {
    const pickState = singleBet.picks[pickId?.toString()];

    if (!pickState) {
        return getEmptyValidationInput(BetslipType.Single);
    }

    const selectedPicks = pickState.isSelected ? pickList.filter((p) => p.id.isEqual(pickId)) : [];

    return {
        selectedPicks,
        allPicks: pickList ?? [],
        selectedType: BetslipType.Single,
        hasEachWay: pickState.isEachWay ?? false,
        stake: calculateStateStake(pickState),
        teaserTotalOdds: undefined,
    };
}

export function getBetBuilderValidationInput(betBuilder: IBetBuilderBetState, pickList: BetslipPick[], pickId: PickId): TokenValidationInput {
    const pickState = betBuilder.picks[pickId?.toString()];

    if (!pickState) {
        return getEmptyValidationInput(BetslipType.BetBuilder);
    }

    const selectedPicks = pickState.isSelected ? pickList.filter((p) => p.id.isEqual(pickId)) : [];

    return {
        selectedPicks,
        allPicks: pickList ?? [],
        selectedType: BetslipType.BetBuilder,
        hasEachWay: false,
        stake: calculateStateStake(pickState),
        teaserTotalOdds: undefined,
    };
}

export function getEmptyValidationInput(betslipType: BetslipType): TokenValidationInput {
    return {
        selectedPicks: [],
        allPicks: [],
        selectedType: betslipType,
        hasEachWay: false,
        stake: new Decimal(0),
        teaserTotalOdds: undefined,
    };
}

export function calculateStateStake(state: { stake: number | null; isEachWay?: boolean }) {
    const eachWayFactor = state.isEachWay ? 2 : 1;
    const stake = state.stake ?? 0;

    return new Decimal(eachWayFactor * stake);
}

export function getBetStationTokenValidationInput(state: ValidationInputState): TokenValidationInput {
    const { types, pickList } = state;

    const selectedType = types?.base?.currentSelectedType;

    return {
        selectedPicks: getBetStationSelectedPicks(state),
        allPicks: pickList ?? [],
        selectedType,
        hasEachWay: isBetStationEachWaySelected(state),
        stake: getBetStationStake(state),
        teaserTotalOdds: undefined,
    };
}

export function isBetStationEachWaySelected({ types }: ValidationInputState): boolean {
    switch (types.base?.currentSelectedType) {
        case BetslipType.Single: {
            return Object.values(types.singleBet?.picks ?? {}).some((pick) => pick.isSelected && pick.isEachWay);
        }
        case BetslipType.Combo: {
            return types.comboBet?.isEachWay;
        }
        case BetslipType.System: {
            return types.systemBet?.isEachWay;
        }
        default:
            return false;
    }
}

export function getBetStationStake({ types }: ValidationInputState) {
    const selectedType = types?.base?.currentSelectedType;
    if (selectedType === BetslipType.Single) {
        const selectedPick = Object.values(types?.singleBet?.picks ?? {}).find((t) => t.isSelected);
        if (!selectedPick) {
            return new Decimal(0);
        }

        return new Decimal((selectedPick.isEachWay ? 2 : 1) * (selectedPick.stake ?? 0));
    }
    if (selectedType === BetslipType.Combo) {
        return new Decimal((types?.comboBet?.isEachWay ? 2 : 1) * (types?.comboBet?.stake ?? 0));
    }

    return new Decimal(0);
}

export function getBetStationSelectedPicks({ types, pickList }: ValidationInputState): BetslipPick[] {
    if (types.base?.currentSelectedType === BetslipType.Combo) {
        const comboPicksId = Object.entries(types.comboBet?.picks ?? {});
        const selectedComboPickIds = comboPicksId.filter(([_, pick]) => pick.isSelected).map(([id]) => id);

        return pickList?.filter(({ id }) => selectedComboPickIds.includes(id.toString()));
    }

    const singleBetPicks = Object.entries(types.singleBet?.picks ?? {});

    const selectedPickIds = singleBetPicks.filter(([_, pick]) => pick.isSelected).map(([id]) => id);

    return pickList?.filter(({ id }) => selectedPickIds.includes(id.toString()));
}
